import moment from "moment";
import { truncateWords } from "../../../../utils/helpers";

const formatRolesNotSigned = (value: string): string => {
  if (!value || value === '-') return '-';

  // Split comma-separated roles and trim whitespace
  const roles = value.split(',')
    .map((role) => role.trim())
    .filter((role) => role); // Remove any empty strings

  return roles.join(', ');
};

const createColumns = (shouldApplySorting: boolean = false) => {
  const getColumnConfig = (
    header: string,
    accessor: string,
    cellFormatter: (value: any) => any = (value) => value || "-",
    customSort: boolean = true,
    disableSortBy: boolean = false,
    optional: boolean = false,
    filterType: string = "",
    customCellType: string = ""
  ) => ({
    Header: header,
    accessor: accessor,
    Cell: ({ value }: any) => cellFormatter(value),
    sortType:
      shouldApplySorting && customSort
        ? customSortWithinResidentGroup
        : "basic",
    disableSortBy: disableSortBy,
    optional: optional,
    filterType: filterType,
    customCellType: customCellType,
  });

  // FacilityDate and FacilityTime are the values the nurse entered at the time of the incident
  const baseColumns = [
    getColumnConfig("Resident Name", "ResidentName", truncateWords, false, false, false, "checkbox"),
    getColumnConfig("Date", "FacilityDate", (value) =>
      moment(value).format("M/DD/YYYY"), false, false, false, "checkbox"
    ),
    getColumnConfig("Time", "FacilityTime", (value) =>
      parseAndFormatTime(value)
    ),
    getColumnConfig("Activity", "Activity", ((value) =>
      value?.length ? truncateWords(value, 17) : "-"), false, false, false, "checkbox"
    ),
    getColumnConfig("Location", "Location", undefined, false, false, false, "checkbox"),
  ];

  const additionalColumns = {
    injuries: getColumnConfig("Injuries", "Injuries"),
    transferToHospital: getColumnConfig("Transfer to Hospital", "SentToAcuteCareFacility"),
    rolesNotSigned: getColumnConfig("Roles Not Signed", "RolesNotSigned", formatRolesNotSigned, false, false, false, "rolesNotSigned"),
    unit: getColumnConfig("Unit", "PCCUnitDescription", undefined, false, false, false, "checkbox"),
    bed: getColumnConfig("Bed", "PCCBedDescription", undefined, false, false, false, "checkbox"),
    room: getColumnConfig("Room", "PCCRoomDescription", undefined, false, false, false, "checkbox"),
    floor: getColumnConfig("Floor", "PCCFloorDescription", undefined, false, false, false, "checkbox"),
    incidentType: getColumnConfig("Type", "IncidentType", undefined, false, false, false, "checkbox"),
    facilityDate: getColumnConfig("Date", "FacilityDate"),
    facilityTime: getColumnConfig("Time", "FacilityTime"),
    view: getColumnConfig("View", "IncidentId", undefined, false, true, false, "", "view"),
    numberOfIncidents: getColumnConfig("Number of Incidents", "NumberOfIncident"),
    place: getColumnConfig("Location", "Place"),
    dayOfWeek: getColumnConfig("Day of Week", "DayOfTheWeek", undefined, false, false, false, "checkbox"),
    shift: getColumnConfig("Shift", "Shift"),
    skinType: getColumnConfig("Skin Type", "SkinIssueType"),
    ulcerType: getColumnConfig("Ulcer Type", "UlcerType"),
    fallType: getColumnConfig("Fall Type", "FallType"),
    incidentBehavior: getColumnConfig("Behavior", "BehaviorExhibited"),
    typeOfAllegation: getColumnConfig("Type", "TypeOfAllegation"),
    createdBy: getColumnConfig("Created By", "CreatedBy"),
    caregiverRoles: getColumnConfig("Role(s)", "Roles", undefined, false, false, false, "checkbox"),
    facility: getColumnConfig("Facility", "Facility"),
    status: getColumnConfig("Status", "Status", undefined, false, false, false, "checkbox"),
    score: getColumnConfig("Score", "Score", undefined, false, false, true),
    riskRating: getColumnConfig("Risk Rating", "RiskRating", undefined, false, false, true, "checkbox"),
    dOHNotified: getColumnConfig("DOH Notified", "DOHNotified", undefined, false, false, true, "checkbox"),
    injury: getColumnConfig("Injuries", "Injuries", undefined, false, false, true, "checkbox"),
    sentToAcuteCareFacility: getColumnConfig("Sent to Acute Care Facility", "SentToAcuteCareFacility", undefined, false, false, true, "checkbox"),
    incidentName: getColumnConfig("Incident", "IncidentName", undefined, false, false, false, "", "incident"),
    sortableIncidentDate: getColumnConfig("Date", "FacilityDate", undefined, false, false, false, "", "date"),
    caregiverBreakdownLocation: getColumnConfig("Location", "Location", undefined, false, false, true, "checkbox"),
    // Plain FacilityDate (already "MM/DD/YYYY" from the API), NOT the "date" customCellType —
    // SortableDateCell combines FacilityDate + FacilityTime, and the breakdown payload has no
    // FacilityTime, which rendered every row blank.
    caregiverBreakdownDate: getColumnConfig("Date", "FacilityDate", undefined, false, false, false, "checkbox"),
    caregiverBreakdownActivity: getColumnConfig("Activity", "Activity", ((value) =>
      value?.length ? truncateWords(value, 17) : "-"), false, false, true, "checkbox"
    ),
    // Caregiver Audit breakdown (MARCC-773/788) role columns: name highlights via
    // DynamicGrid's "matchedCaregiver" cell type when it matches a selected caregiver.
    matchedInitialReporter: getColumnConfig("Who Initially Reported Incident", "InitialReporter", undefined, false, false, false, "checkbox", "matchedCaregiver"),
    matchedNurseAssigned: getColumnConfig("Nurse Assigned", "NurseAssigned", undefined, false, false, false, "checkbox", "matchedCaregiver"),
    matchedCNAAssigned: getColumnConfig("CNA Assigned", "CNAAssigned", undefined, false, false, false, "checkbox", "matchedCaregiver"),
    // "rolesNotSigned" filter (generic despite the name): this value is comma-joined, so it
    // splits into one option per person instead of matching the whole joined string.
    matchedWhoLastProvidedCare: getColumnConfig("Who Last Provided Care", "WhoLastProvidedCare", undefined, false, false, false, "rolesNotSigned", "matchedCaregiver"),
  };

  const woundAnalysisColumns = {
    woundResidentName: getColumnConfig("Resident Name", "residentName"),
    woundUnit: getColumnConfig("Unit", "unit"),
    woundDate: getColumnConfig("Date", "facilityDate"),
    woundTime: getColumnConfig("Time", "facilityTime"),
    woundHistory: getColumnConfig("Wound History", "woundHistory"),
    woundPresent: getColumnConfig("Wound Present", "woundPresent"),
    skinIssueType: getColumnConfig("Skin Issue Type", "skinIssueType"),
    skinTypeNonUlcer: getColumnConfig("Non-Ulcer Type", "skinIssueTypeNonUlcer"),
    skinTypeUlcer: getColumnConfig("Ulcer Type", "skinIssueTypeUlcer"),
    woundLocation: getColumnConfig("Location", "location"),
    woundIncidentNumber: getColumnConfig("Incident", "incidentNumber", undefined, false, false, false, "", "incident"),
  };

  const injuryReportColumns = {
    injuryIncidentName: getColumnConfig("Incident #", "IncidentName", undefined, false, false, false, "", "incident"),
    injuryIncidentDate: getColumnConfig("Date", "FacilityDate", undefined, false, false, false, "checkbox"),
    injuryIncidentTime: getColumnConfig("Time", "FacilityTime", undefined, false, false, false, "checkbox"),
    injuryIncidentType: getColumnConfig("Type", "IncidentType", undefined, false, false, false, "checkbox"),
    injuryIncidentLocation: getColumnConfig("Location", "IncidentLocation", undefined, false, false, false, "checkbox"),
    injuryType: getColumnConfig("Injury Type", "InjuryType", undefined, false, false, false, "checkbox"),
    injuryArea: getColumnConfig("Area", "InjuryArea", undefined, false, false, false, "checkbox"),
    injuryLocation: getColumnConfig("Injury Location", "InjuryLocation", undefined, false, false, false, "checkbox"),
    injurySide: getColumnConfig("Side", "InjurySide", undefined, false, false, false, "checkbox"),
    injuryDescription: getColumnConfig("Description", "InjuryDescription", undefined, false, false, true, "checkbox"),
    injurySentToAcuteCare: getColumnConfig("Sent to Acute Care Facility", "SentToAcuteCareFacility", undefined, false, false, false, "checkbox"),
  };

  const behaviorAnalysisColumns = {
    // Columns for modal drilldowns (lowercase accessors matching drilldown data)
    behaviorResidentName: getColumnConfig("Resident Name", "residentName"),
    behaviorUnit: getColumnConfig("Unit", "unit"),
    behaviorDate: getColumnConfig("Date", "facilityDate"),
    behaviorTime: getColumnConfig("Time", "facilityTime"),
    behaviorType: getColumnConfig("Behavior Type", "behaviorType"),
    behaviorLocation: getColumnConfig("Location", "location"),
    behaviorInjury: getColumnConfig("Injury", "injury"),
    behaviorIncidentNumber: getColumnConfig("Incident #", "incidentNumber", undefined, false, false, false, "", "incident"),
    // Columns for incident list (camelCase accessors matching incidentList API data)
    listResidentName: getColumnConfig("Resident Name", "residentName", undefined, false, false, false, "checkbox"),
    listIncidentType: getColumnConfig("Incident Type", "incidentType", undefined, false, false, false, "checkbox"),
    listBehaviorType: getColumnConfig("Behavior Type", "behaviorType", undefined, false, false, false, "checkbox"),
    listDate: getColumnConfig("Date", "date", undefined, false, false, false, "checkbox"),
    listTime: getColumnConfig("Time", "time"),
    listShift: getColumnConfig("Shift", "generalShift", undefined, false, false, true, "checkbox"),
    listLocation: getColumnConfig("Location", "location", undefined, false, false, false, "checkbox"),
    listUnit: getColumnConfig("Unit", "unit", undefined, false, false, false, "checkbox"),
    listTargetOfAggression: getColumnConfig("Target of Aggression", "targetOfAggression", undefined, false, false, false, "checkbox"),
    listInjury: getColumnConfig("Injury", "injury", undefined, false, false, false, "checkbox"),
    listDOHReportable: getColumnConfig("DOH Reportable", "dohReportable", undefined, false, false, true, "checkbox"),
    listHosp: getColumnConfig("Hosp", "hosp", undefined, false, false, true, "checkbox"),
    listStatus: getColumnConfig("Status", "status", undefined, false, false, true, "checkbox"),
    listIncidentNumber: getColumnConfig("Incident #", "incidentNumber", undefined, false, false, false, "", "incident"),
  };

  return {
    baseColumns,
    additionalColumns,
    woundAnalysisColumns,
    injuryReportColumns,
    behaviorAnalysisColumns,
  };
};

export const incidentLists = () => {
  const { baseColumns, additionalColumns } = createColumns(false);
  return [
    ...baseColumns,
    additionalColumns.unit,
    additionalColumns.bed,
    additionalColumns.room,
    additionalColumns.floor,
    additionalColumns.incidentType,
    additionalColumns.view,
  ];
};

// Caregiver Audit (MARCC-773) drill-down: which role(s) the caregiver held per incident.
export const caregiverAuditDetailCols = () => {
  const { baseColumns, additionalColumns } = createColumns(false);
  return [
    baseColumns[0], // Resident Name
    additionalColumns.caregiverRoles,
    additionalColumns.incidentType,
    baseColumns[1], // Date
    additionalColumns.unit,
    additionalColumns.status,
    additionalColumns.view,
  ];
};

// Caregiver Audit breakdown (MARCC-773/788): one row per incident, all four role
// columns. selectedIds (Contact_on_Facility__c Ids) is threaded into the four role
// columns so DynamicGrid's "matchedCaregiver" cell can highlight the currently-
// selected caregiver(s) wherever they appear.
//
// The last five are `optional: true`, so DynamicGrid renders them as its own
// "Include Columns" toggles — same mechanism the Incident List report uses.
export const caregiverAuditBreakdownCols = (selectedIds: Set<string>) => {
  const { baseColumns, additionalColumns } = createColumns(false);
  const withSelectedIds = (col: any) => ({ ...col, selectedIds });

  return [
    baseColumns[0], // Resident Name
    additionalColumns.incidentType,
    additionalColumns.caregiverBreakdownDate,
    additionalColumns.unit,
    withSelectedIds(additionalColumns.matchedInitialReporter),
    withSelectedIds(additionalColumns.matchedNurseAssigned),
    withSelectedIds(additionalColumns.matchedCNAAssigned),
    withSelectedIds(additionalColumns.matchedWhoLastProvidedCare),
    additionalColumns.dOHNotified,
    additionalColumns.injury,
    additionalColumns.sentToAcuteCareFacility,
    additionalColumns.caregiverBreakdownLocation,
    additionalColumns.caregiverBreakdownActivity,
    additionalColumns.view,
  ];
};

export const incidentListReport = () => {
  const { baseColumns, additionalColumns } = createColumns(false);
  return [
    baseColumns[0], // Name
    additionalColumns.incidentType,
    additionalColumns.facilityDate, // Use FacilityDate for date
    additionalColumns.facilityTime, // Use FacilityTime for time
    baseColumns[3], // Activity
    additionalColumns.dOHNotified,
    additionalColumns.injury,
    additionalColumns.sentToAcuteCareFacility,
    baseColumns[4], // Location
    additionalColumns.unit,
    additionalColumns.bed,
    additionalColumns.room,
    additionalColumns.score,
    additionalColumns.riskRating,
    additionalColumns.status,
    additionalColumns.incidentName,
  ];
};

export const woundAnalysisDetailCols = () => {
  const { woundAnalysisColumns } = createColumns(false);
  return [
    woundAnalysisColumns.woundResidentName,
    woundAnalysisColumns.woundUnit,
    woundAnalysisColumns.woundDate,
    woundAnalysisColumns.woundTime,
    woundAnalysisColumns.skinIssueType,
    woundAnalysisColumns.skinTypeNonUlcer,
    woundAnalysisColumns.skinTypeUlcer,
    woundAnalysisColumns.woundHistory,
    woundAnalysisColumns.woundPresent,
    woundAnalysisColumns.woundIncidentNumber,
  ];
};

// PU Staging drill-down: drops the Non-Ulcer Type column (always blank for pressure ulcers) and
// adds Location. (Per MARCC-654 feedback — only this grid's drill-down differs from the default.)
export const pressureUlcerStagingDetailCols = () => {
  const { woundAnalysisColumns } = createColumns(false);
  return [
    woundAnalysisColumns.woundResidentName,
    woundAnalysisColumns.woundUnit,
    woundAnalysisColumns.woundDate,
    woundAnalysisColumns.woundTime,
    woundAnalysisColumns.skinIssueType,
    woundAnalysisColumns.skinTypeUlcer,
    woundAnalysisColumns.woundLocation,
    woundAnalysisColumns.woundHistory,
    woundAnalysisColumns.woundPresent,
    woundAnalysisColumns.woundIncidentNumber,
  ];
};

export const behaviorAnalysisDetailCols = () => {
  const { behaviorAnalysisColumns } = createColumns(false);
  return [
    behaviorAnalysisColumns.behaviorResidentName,
    behaviorAnalysisColumns.behaviorUnit,
    behaviorAnalysisColumns.behaviorDate,
    behaviorAnalysisColumns.behaviorTime,
    behaviorAnalysisColumns.behaviorType,
    behaviorAnalysisColumns.behaviorLocation,
    behaviorAnalysisColumns.behaviorInjury,
    behaviorAnalysisColumns.behaviorIncidentNumber,
  ];
};

// Full incident list for BehaviorIncidentList component with all columns and filters
export const behaviorIncidentListCols = () => {
  const { behaviorAnalysisColumns } = createColumns(false);
  return [
    behaviorAnalysisColumns.listResidentName,
    behaviorAnalysisColumns.listIncidentType,
    behaviorAnalysisColumns.listBehaviorType,
    behaviorAnalysisColumns.listDate,
    behaviorAnalysisColumns.listTime,
    behaviorAnalysisColumns.listShift,
    behaviorAnalysisColumns.listLocation,
    behaviorAnalysisColumns.listUnit,
    behaviorAnalysisColumns.listTargetOfAggression,
    behaviorAnalysisColumns.listInjury,
    behaviorAnalysisColumns.listDOHReportable,
    behaviorAnalysisColumns.listHosp,
    behaviorAnalysisColumns.listStatus,
    behaviorAnalysisColumns.listIncidentNumber,
  ];
};

export const injuryReportCols = () => {
  const { injuryReportColumns } = createColumns(false);
  return [
    injuryReportColumns.injuryIncidentName,
    injuryReportColumns.injuryIncidentDate,
    injuryReportColumns.injuryIncidentTime,
    injuryReportColumns.injuryIncidentType,
    injuryReportColumns.injuryIncidentLocation,
    injuryReportColumns.injuryType,
    injuryReportColumns.injuryArea,
    injuryReportColumns.injuryLocation,
    injuryReportColumns.injurySide,
    injuryReportColumns.injuryDescription,
    injuryReportColumns.injurySentToAcuteCare,
  ];
};

export const statusReport = () => {
  const { baseColumns, additionalColumns } = createColumns(false);
  return [
    additionalColumns.status, // Add the status column here
    additionalColumns.rolesNotSigned,
    baseColumns[0],
    additionalColumns.facilityDate, // Use FacilityDate for date
    additionalColumns.facilityTime, // Use FacilityTime for time
    baseColumns[3],
    baseColumns[4],
    additionalColumns.incidentType,
    additionalColumns.incidentName,
  ];
};

export const similarRepeatCols = (shouldApplySorting: boolean = false) => {
  const { baseColumns, additionalColumns } = createColumns(shouldApplySorting);
  return [
    ...baseColumns,
    additionalColumns.numberOfIncidents,
    additionalColumns.place,
    additionalColumns.dayOfWeek,
    additionalColumns.shift,
  ];
};

export const repeatSkinCols = (shouldApplySorting: boolean = false) => {
  const { baseColumns, additionalColumns } = createColumns(shouldApplySorting);
  const indexOfActivity = baseColumns.findIndex(
    (col) => col.Header === "Activity"
  );
  return [
    ...baseColumns.slice(0, indexOfActivity + 1),
    additionalColumns.skinType,
    additionalColumns.ulcerType,
    ...baseColumns.slice(indexOfActivity + 1),
  ];
};

export const repeatFallCols = (shouldApplySorting: boolean = false) => {
  const { baseColumns, additionalColumns } = createColumns(shouldApplySorting);
  const indexOfActivity = baseColumns.findIndex(
    (col) => col.Header === "Activity"
  );
  return [
    ...baseColumns.slice(0, indexOfActivity + 1),
    additionalColumns.fallType,
    ...baseColumns.slice(indexOfActivity + 1),
  ];
};

export const repeatIncidentsCol = (shouldApplySorting: boolean = false) => {
  const { baseColumns, additionalColumns } = createColumns(shouldApplySorting);
  const indexOfActivity = baseColumns.findIndex(
    (col) => col.Header === "Activity"
  );
  return [
    ...baseColumns.slice(0, indexOfActivity + 1),
    additionalColumns.typeOfAllegation,
    additionalColumns.incidentBehavior,
    ...baseColumns.slice(indexOfActivity + 1),
  ];
};

export const repeatIncidentReport = () => {
  const { baseColumns, additionalColumns } = createColumns(false);
  return [
    additionalColumns.incidentName, // "Incident #" with incident link
    additionalColumns.incidentType, // "Incident Type"
    additionalColumns.fallType, // "Fall Type"
    additionalColumns.dayOfWeek, // Day of Week
    additionalColumns.facilityDate, // "Date" using FacilityDate
    additionalColumns.facilityTime, // "Time" using FacilityTime
    baseColumns[4], // Location
    additionalColumns.unit, // "Unit"
    additionalColumns.injury, // "Injury"
    additionalColumns.sentToAcuteCareFacility, // "Sent to Acute Care Facility"
  ];
};

export const incidentListCols = (
  shouldApplySorting: boolean = false,
  showMarkReadyColumn: boolean = false
) => {
  const getColumnConfig = (
    header: string,
    accessor: string,
    cellFormatter: (value: any) => any = (value) => value || "-",
    customSort: boolean = true,
    disableSortBy: boolean = false,
    optional: boolean = false,
    filterType: string = "",
    customCellType: string = ""
  ) => ({
    Header: header,
    accessor: accessor,
    Cell: ({ value }: any) => cellFormatter(value),
    customSort,
    disableSortBy: disableSortBy,
    optional: optional,
    filterType: filterType,
    customCellType: customCellType,
  });

  const baseColumns = [
    getColumnConfig("Incident", "name", undefined, true, false, false, "", "draft"),
    getColumnConfig("Type", "type", undefined, true, false, false, "checkbox"),
    getColumnConfig("Status", "status"),
    getColumnConfig("Facility", "facility"),
    getColumnConfig("Resident", "resident"),
    getColumnConfig("Date", "FacilityDate"),
    getColumnConfig("Time", "FacilityTime"),
    getColumnConfig("Created By", "createdBy"),
  ];

  // The 'mark ready' action is now handled on the incident detail page.
  // Keep the function signature for backwards compatibility but ignore showMarkReadyColumn.
  return baseColumns;
};

const customSortWithinResidentGroup = (
  rowA: any,
  rowB: any,
  columnId: string
) => {
  if (rowA.original.ResidentName !== rowB.original.ResidentName) {
    return false;
  }
  if (rowA.original[columnId] === rowB.original[columnId]) {
    return 1;
  }
  if (rowA.original[columnId] < rowB.original[columnId]) {
    return -1;
  }
  return 0;
};

export const parseAndFormatTime = (value: string): string => {
  if (!value) return "-";
  // Remove trailing ":00.000Z" if present
  const timePart = value.split(":00.000Z")[0];
  let combined = moment(`1970-01-08T${timePart}`);
  return combined.isValid() ? combined.format("HH:mm") : "-";
};
